import { useEffect, useMemo, useRef, useState } from 'react'
import { apiFetch } from '../lib/api'

function formatTime(value) {
	if (!value) return ''
	const date = new Date(value)
	return date.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

export default function Reels({ currentUser, currentUserId, requests, messages, socket, onLogin, onOpenProfile, focusReelId = null, onFocusHandled }) {
	const [reels, setReels] = useState([])
	const [loading, setLoading] = useState(true)
	const [likesModalPost, setLikesModalPost] = useState(null)
	const [commentsModalPost, setCommentsModalPost] = useState(null)
	const [likesModalLoading, setLikesModalLoading] = useState(false)
	const [commentsModalLoading, setCommentsModalLoading] = useState(false)
	const [modalLikes, setModalLikes] = useState([])
	const [modalComments, setModalComments] = useState([])
	const [modalCommentDraft, setModalCommentDraft] = useState('')
	const [modalCommentSubmitting, setModalCommentSubmitting] = useState(false)
	const [shareTarget, setShareTarget] = useState(null)
	const [shareSubmittingId, setShareSubmittingId] = useState(null)
	const [currentIndex, setCurrentIndex] = useState(0)
	const [isPaused, setIsPaused] = useState(false)
	const videoRef = useRef(null)
	const touchStartYRef = useRef(null)
	const wheelLockRef = useRef(false)

	useEffect(() => {
		loadReels()
	}, [])

	async function loadReels() {
		setLoading(true)
		try {
			const res = await apiFetch('/reels?limit=12')
			if (!res.ok) throw new Error('Failed to load reels')
			const data = await res.json()
			const onlyVideoReels = (Array.isArray(data.posts) ? data.posts : []).filter(
				(item) => item?.feedType === 'REEL' && item?.mediaType === 'VIDEO' && !!item?.mediaUrl
			)
			setReels(onlyVideoReels)
			setCurrentIndex(0)
			setIsPaused(false)
		} catch {
			setReels([])
			setCurrentIndex(0)
			setIsPaused(false)
		} finally {
			setLoading(false)
		}
	}

	function stepReel(direction) {
		if (!reels.length) return
		setIsPaused(false)
		setCurrentIndex((prev) => {
			if (direction > 0) return Math.min(reels.length - 1, prev + 1)
			return Math.max(0, prev - 1)
		})
	}

	function handleWheel(event) {
		event.preventDefault()
		if (wheelLockRef.current) return
		if (Math.abs(event.deltaY) < 12) return
		wheelLockRef.current = true
		stepReel(event.deltaY > 0 ? 1 : -1)
		window.setTimeout(() => {
			wheelLockRef.current = false
		}, 280)
	}

	function handleTouchStart(event) {
		touchStartYRef.current = event.touches?.[0]?.clientY ?? null
	}

	function handleTouchEnd(event) {
		const startY = touchStartYRef.current
		const endY = event.changedTouches?.[0]?.clientY ?? null
		touchStartYRef.current = null
		if (startY == null || endY == null) return
		const deltaY = startY - endY
		if (Math.abs(deltaY) < 40) return
		stepReel(deltaY > 0 ? 1 : -1)
	}

	function handleTouchMove(event) {
		event.preventDefault()
	}

	function handleKeyDown(event) {
		if (event.key === 'ArrowDown') {
			event.preventDefault()
			stepReel(1)
		}
		if (event.key === 'ArrowUp') {
			event.preventDefault()
			stepReel(-1)
		}
	}

	async function toggleLike(postId) {
		if (!currentUser) return onLogin?.()
		const existingPost = reels.find((post) => post.id === postId)
		if (!existingPost) return

		const previousLiked = !!existingPost.likedByMe
		const previousCount = Number(existingPost.likesCount || 0)
		const optimisticLiked = !previousLiked
		const optimisticCount = Math.max(0, previousCount + (optimisticLiked ? 1 : -1))

		setReels((prev) => prev.map((post) => (
			post.id === postId
				? { ...post, likedByMe: optimisticLiked, likesCount: optimisticCount }
				: post
		)))

		try {
			const res = await apiFetch(`/reels/${postId}/like`, { method: 'POST' })
			if (!res.ok) throw new Error('Failed to toggle like')
			const data = await res.json()
			setReels((prev) => prev.map((post) => (
				post.id === postId
					? { ...post, likedByMe: data.liked, likesCount: data.likesCount }
					: post
			)))
		} catch {
			setReels((prev) => prev.map((post) => (
				post.id === postId
					? { ...post, likedByMe: previousLiked, likesCount: previousCount }
					: post
			)))
		}
	}

	function handleDoubleClick(post, event) {
		const target = event.target
		if (target instanceof Element && target.closest('button, input, textarea, a, label')) return
		if (post.likedByMe) return
		toggleLike(post.id)
	}

	async function openLikesModal(post) {
		setLikesModalPost(post)
		setLikesModalLoading(true)
		setModalLikes([])
		try {
			const res = await apiFetch(`/reels/${post.id}/likes`)
			if (!res.ok) throw new Error('Failed to load likes')
			const data = await res.json()
			setModalLikes(Array.isArray(data.likes) ? data.likes : [])
		} catch {
			setModalLikes([])
		} finally {
			setLikesModalLoading(false)
		}
	}

	async function openCommentsModal(post) {
		setCommentsModalPost(post)
		setCommentsModalLoading(true)
		setModalComments([])
		setModalCommentDraft('')
		try {
			const res = await apiFetch(`/reels/${post.id}/comments`)
			if (!res.ok) throw new Error('Failed to load comments')
			const data = await res.json()
			setModalComments(Array.isArray(data.comments) ? data.comments : [])
		} catch {
			setModalComments([])
		} finally {
			setCommentsModalLoading(false)
		}
	}

	async function submitModalComment() {
		if (!currentUser) return onLogin?.()
		if (!commentsModalPost) return

		const text = modalCommentDraft.trim()
		if (!text) return

		setModalCommentSubmitting(true)
		try {
			const res = await apiFetch(`/reels/${commentsModalPost.id}/comments`, {
				method: 'POST',
				body: JSON.stringify({ text }),
			})
			if (!res.ok) return
			const comment = await res.json()
			setModalComments((prev) => [comment, ...prev])
			setReels((prev) => prev.map((post) => (
				post.id === commentsModalPost.id
					? { ...post, commentsCount: comment.commentsCount, comments: [comment, ...(post.comments || [])] }
					: post
			)))
			setModalCommentDraft('')
		} finally {
			setModalCommentSubmitting(false)
		}
	}

	const shareChats = useMemo(() => {
		return requests
			.filter((request) => request.status === 'ACCEPTED')
			.map((request) => {
				const otherUser = request.senderId === currentUserId ? request.receiver : request.sender
				const chatMessages = messages[request.id] || []
				const latestMessage = chatMessages.at(-1)
				return {
					id: request.id,
					otherUser,
					latestAt: latestMessage?.createdAt || request.updatedAt || request.createdAt || null,
					preview: latestMessage?.text || otherUser?.location?.area || 'Start chatting',
				}
			})
			.sort((a, b) => new Date(b.latestAt || 0).getTime() - new Date(a.latestAt || 0).getTime())
	}, [currentUserId, messages, requests])

	async function shareReelToChat(chat) {
		if (!currentUser) return onLogin?.()
		if (!shareTarget || !chat?.otherUser?.id) return

		setShareSubmittingId(chat.id)
		try {
			const payload = {
				requestId: chat.id,
				receiverId: chat.otherUser.id,
				text: shareTarget.content ? shareTarget.content : 'Shared a reel from Co-Eater',
				mediaUrl: shareTarget.mediaUrl,
				mediaType: 'reel',
				fileName: String(shareTarget.id),
				replyToId: null,
			}

			if (socket?.connected) {
				socket.emit('send_message', payload)
			} else {
				await apiFetch('/messages', {
					method: 'POST',
					body: JSON.stringify({
						...payload,
						senderId: currentUserId,
					}),
				})
			}
			setShareTarget(null)
		} finally {
			setShareSubmittingId(null)
		}
	}

	const activeReel = reels[currentIndex] || null

	useEffect(() => {
		if (!focusReelId || !reels.length) return
		const numericId = Number(focusReelId)
		if (!Number.isFinite(numericId)) {
			onFocusHandled?.()
			return
		}
		const targetIndex = reels.findIndex((item) => Number(item.id) === numericId)
		if (targetIndex >= 0) {
			setCurrentIndex(targetIndex)
			setIsPaused(false)
		}
		onFocusHandled?.()
	}, [focusReelId, reels, onFocusHandled])

	useEffect(() => {
		const video = videoRef.current
		if (!video) return
		if (isPaused) {
			video.pause()
			return
		}
		const playPromise = video.play()
		if (playPromise && typeof playPromise.catch === 'function') {
			playPromise.catch(() => {})
		}
	}, [activeReel?.id, isPaused])

	return (
		<section className="mx-auto w-full max-w-2xl">
			{/* <div className="mb-4 flex items-center justify-between rounded-3xl border border-white/60 bg-white/80 px-5 py-4 shadow-lg backdrop-blur">
				<div>
					<p className="text-xs font-semibold uppercase tracking-[0.2em] text-orange-600">Short Videos</p>
					<h2 className="title-font text-2xl font-bold text-gray-900">Reels</h2>
				</div>
				<button onClick={loadReels} className="rounded-full border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50">
					Refresh
				</button>
			</div> */}

			<div className="space-y-6">
				{loading && <p className="text-sm font-semibold text-gray-600">Loading reels...</p>}
				{!loading && reels.length === 0 && (
					<div className="rounded-3xl border border-gray-200 bg-white p-5 text-sm text-gray-600">
						No reels yet. Upload the first one from the post composer.
					</div>
				)}

				{activeReel && (
					<article
						key={activeReel.id}
						onDoubleClick={(event) => handleDoubleClick(activeReel, event)}
						onWheel={handleWheel}
						onTouchStart={handleTouchStart}
						onTouchMove={handleTouchMove}
						onTouchEnd={handleTouchEnd}
						onKeyDown={handleKeyDown}
						tabIndex={0}
						className="relative mx-auto w-full max-w-[420px] overflow-hidden rounded-[2rem] border border-white/30 bg-black shadow-2xl"
						style={{ touchAction: 'none' }}
					>
						<div className="relative h-[76vh] min-h-[520px] w-full max-h-[860px]">
							<video ref={videoRef} key={activeReel.id} autoPlay loop playsInline className="h-full w-full object-cover">
								<source src={activeReel.mediaUrl} type="video/mp4" />
							</video>
							<div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/15 to-black/0" />

							<div className="absolute bottom-0 left-0 right-0 flex items-end justify-between gap-3 p-4">
								<div className="min-w-0 flex-1 text-white">
									<div className="flex items-center gap-2">
										<button type="button" onClick={() => onOpenProfile?.(activeReel.user?.id)} className="shrink-0">
											<img
												src={activeReel.user?.profilePicture || `https://api.dicebear.com/7.x/thumbs/svg?seed=${encodeURIComponent(activeReel.user?.name || 'user')}`}
												alt={activeReel.user?.name || 'User'}
												className="h-10 w-10 rounded-full border border-white/40 object-cover"
											/>
										</button>
										<button type="button" onClick={() => onOpenProfile?.(activeReel.user?.id)} className="truncate text-left text-sm font-semibold text-white">
											{activeReel.user?.name}
										</button>
										<p className="text-xs text-white/80">{formatTime(activeReel.createdAt)}</p>
									</div>
									{activeReel.content && <p className="mt-2 line-clamp-3 text-sm text-white/95">{activeReel.content}</p>}
									{activeReel.restaurant && <p className="mt-1 text-xs font-semibold text-orange-200">{activeReel.restaurant.name} • {activeReel.restaurant.area}</p>}
								</div>

								<div className="mb-1 flex flex-col items-center gap-3 text-white">
									<button onClick={() => toggleLike(activeReel.id)} className="flex h-10 w-10 items-center justify-center rounded-full bg-black/35 text-lg">
										{activeReel.likedByMe ? '♥' : '♡'}
									</button>
									<button onClick={() => openLikesModal(activeReel)} className="text-xs font-semibold text-white">{activeReel.likesCount || 0}</button>
									<button onClick={() => openCommentsModal(activeReel)} className="flex h-10 w-10 items-center justify-center rounded-full bg-black/35 text-lg">💬</button>
									<button onClick={() => setIsPaused((prev) => !prev)} className="flex h-10 w-10 items-center justify-center rounded-full bg-black/35 text-xs font-bold">{isPaused ? '▶' : '⏸'}</button>
									<button onClick={() => setShareTarget(activeReel)} className="flex h-10 w-10 items-center justify-center rounded-full bg-black/35 text-lg">↗</button>
								</div>
							</div>

							<div className="absolute right-3 top-3 rounded-full bg-black/45 px-3 py-1 text-xs font-semibold text-white">
								{currentIndex + 1} / {reels.length}
							</div>
						</div>
					</article>
				)}
			</div>

			{likesModalPost && (
				<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-3">
					<div className="w-full max-w-md overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-2xl">
						<div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
							<h3 className="text-base font-bold text-gray-900">Liked by</h3>
							<button type="button" onClick={() => setLikesModalPost(null)} className="text-sm font-semibold text-gray-500">Close</button>
						</div>
						<div className="max-h-[60vh] overflow-y-auto p-3">
							{likesModalLoading && <p className="text-sm text-gray-500">Loading likes...</p>}
							{!likesModalLoading && modalLikes.length === 0 && <p className="text-sm text-gray-500">No likes yet.</p>}
							{modalLikes.map((entry) => (
								<div key={entry.id} className="flex items-center gap-3 rounded-xl px-2 py-2">
									<button type="button" onClick={() => onOpenProfile?.(entry.user?.id)} disabled={!entry.user?.id} className="shrink-0">
										<img
											src={entry.user.profilePicture || `https://api.dicebear.com/7.x/thumbs/svg?seed=${encodeURIComponent(entry.user.name)}`}
											alt={entry.user.name}
											className="h-10 w-10 rounded-full object-cover"
										/>
									</button>
									<div className="min-w-0 flex-1">
										<button type="button" onClick={() => onOpenProfile?.(entry.user?.id)} disabled={!entry.user?.id} className="truncate text-left text-sm font-semibold text-gray-900 hover:text-orange-700 disabled:opacity-70">{entry.user.name}</button>
										<p className="text-xs text-gray-500">{formatTime(entry.createdAt)}</p>
									</div>
								</div>
							))}
						</div>
					</div>
				</div>
			)}

			{commentsModalPost && (
				<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-3">
					<div className="flex h-[75vh] w-full max-w-md flex-col overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-2xl">
						<div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
							<h3 className="text-base font-bold text-gray-900">Comments</h3>
							<button type="button" onClick={() => setCommentsModalPost(null)} className="text-sm font-semibold text-gray-500">Close</button>
						</div>
						<div className="flex-1 overflow-y-auto p-3">
							{commentsModalLoading && <p className="text-sm text-gray-500">Loading comments...</p>}
							{!commentsModalLoading && modalComments.length === 0 && <p className="text-sm text-gray-500">No comments yet.</p>}
							{modalComments.map((comment) => (
								<div key={comment.id} className="mb-2 rounded-xl bg-gray-50 px-3 py-2">
									<button type="button" onClick={() => onOpenProfile?.(comment.user?.id)} disabled={!comment.user?.id} className="text-left text-xs font-semibold text-gray-800 hover:text-orange-700 disabled:opacity-70">{comment.user.name}</button>
									<p className="text-sm text-gray-700">{comment.text}</p>
									<p className="mt-1 text-[11px] text-gray-500">{formatTime(comment.createdAt)}</p>
								</div>
							))}
						</div>
						<div className="border-t border-gray-200 p-3">
							<div className="flex gap-2">
								<input
									value={modalCommentDraft}
									onChange={(event) => setModalCommentDraft(event.target.value)}
									className="flex-1 rounded-full border border-gray-300 px-4 py-2 text-sm"
									placeholder="Write a comment..."
								/>
								<button onClick={submitModalComment} disabled={modalCommentSubmitting} className="rounded-full bg-gray-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
									{modalCommentSubmitting ? 'Sending...' : 'Comment'}
								</button>
							</div>
						</div>
					</div>
				</div>
			)}

			{shareTarget && (
				<div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 px-3">
					<div className="w-full max-w-md overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-2xl">
						<div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
							<div>
								<h3 className="text-base font-bold text-gray-900">Send Reel</h3>
								<p className="text-xs text-gray-500">Latest chats appear first.</p>
							</div>
							<button type="button" onClick={() => setShareTarget(null)} className="text-sm font-semibold text-gray-500">Close</button>
						</div>
						<div className="max-h-[60vh] overflow-y-auto p-3">
							{shareChats.length === 0 && <p className="text-sm text-gray-500">No chats available yet.</p>}
							{shareChats.map((chat) => (
								<button
									key={chat.id}
									type="button"
									onClick={() => shareReelToChat(chat)}
									disabled={shareSubmittingId === chat.id}
									className="mb-2 flex w-full items-center gap-3 rounded-2xl border border-gray-200 px-3 py-3 text-left hover:border-orange-300 hover:bg-orange-50 disabled:opacity-60"
								>
									<img
										src={chat.otherUser?.profilePicture || `https://api.dicebear.com/7.x/thumbs/svg?seed=${encodeURIComponent(chat.otherUser?.name || 'user')}`}
										alt={chat.otherUser?.name || 'Chat'}
										className="h-11 w-11 rounded-xl object-cover"
									/>
									<div className="min-w-0 flex-1">
										<p className="truncate text-sm font-semibold text-gray-900">{chat.otherUser?.name}</p>
										<p className="truncate text-xs text-gray-500">{chat.preview}</p>
									</div>
									<span className="text-xs font-semibold text-orange-700">{shareSubmittingId === chat.id ? 'Sending...' : 'Send'}</span>
								</button>
							))}
						</div>
					</div>
				</div>
			)}
		</section>
	)
}